/**
 * Expert Formulated Component
 * Displays information about the expert/doctor who formulated the product
 */

'use client';

import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
 

export interface ExpertFormulatedProps {
  name: string;
  title: string;
  image?: string;
  description: string;
}

const Container = styled.section`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => `${theme.space['3xl']} ${theme.space.md}`};
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${({ theme }) => theme.space['2xl']};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.xl};
    text-align: center;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const ExpertImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.regular};
  object-fit: cover;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.variants.heading2.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  color: ${({ theme }) => theme.colors.pallete.primary.color};
`;

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  }

  br {
    display: block;
    content: "";
    margin: ${({ theme }) => theme.space.sm} 0;
  }
`;

const QuoteIcon = styled.div`
  font-size: ${({ theme }) => theme.typography.variants.heading1.fontSize}px;
  color: ${({ theme }) => theme.colors.pallete.primary.color};
  opacity: 0.3;
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

export function ExpertFormulated({
  name,
  title,
  image,
  description,
}: ExpertFormulatedProps) {
  const theme = useTheme();

  return (
    <Container>
      <Content>
        {image && (
          <ImageWrapper>
            <ExpertImage
              alt={name}
              src={image}
            />
          </ImageWrapper>
        )}
        <TextContent>
          <QuoteIcon>"</QuoteIcon>
          <div>
            <Title>Formulated by {name}</Title>
            <Subtitle>{title}</Subtitle>
          </div>
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </TextContent>
      </Content>
    </Container>
  );
}

